import React, { useState } from 'react';
import Button from 'components/ui/Button';


const AvailabilityCalendar = ({ onDateSelect, selectedDate }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Mock availability data
  const availabilityData = {
    '2025-09-20': { available: true, price: 1200, peak: false },
    '2025-09-21': { available: true, price: 1200, peak: false },
    '2025-09-22': { available: false, price: 1200, peak: false },
    '2025-09-23': { available: true, price: 1500, peak: true },
    '2025-09-24': { available: true, price: 1500, peak: true },
    '2025-09-25': { available: true, price: 1200, peak: false },
    '2025-09-26': { available: true, price: 1200, peak: false },
    '2025-09-27': { available: true, price: 1200, peak: false },
    '2025-09-28': { available: false, price: 1200, peak: false },
    '2025-09-29': { available: true, price: 1500, peak: true },
    '2025-09-30': { available: true, price: 1500, peak: true }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    })?.format(amount);
  };

  const getDaysInMonth = (date) => {
    const year = date?.getFullYear();
    const month = date?.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay?.getDate();
    const startingDayOfWeek = firstDay?.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days?.push(null);
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days?.push(new Date(year, month, day));
    }
    
    return days;
  };

  const navigateMonth = (direction) => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      newMonth?.setMonth(prev?.getMonth() + direction);
      return newMonth;
    });
  };

  const formatDateKey = (date) => {
    return date?.toISOString()?.split('T')?.[0];
  };

  const isToday = (date) => {
    const today = new Date();
    return date?.toDateString() === today?.toDateString();
  };

  const isPastDate = (date) => {
    const today = new Date();
    today?.setHours(0, 0, 0, 0);
    return date < today;
  };

  const isSelectedDate = (date) => {
    if (!selectedDate) return false;
    return formatDateKey(date) === formatDateKey(new Date(selectedDate));
  };

  const handleDateClick = (date) => {
    if (isPastDate(date)) return;
    
    const dateKey = formatDateKey(date);
    const availability = availabilityData?.[dateKey];
    
    if (availability && availability?.available) {
      onDateSelect(dateKey);
    }
  };

  const days = getDaysInMonth(currentMonth);

  return (
    <div className="bg-card rounded-lg shadow-organic p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-heading font-semibold text-card-foreground">
          Select Date
        </h3>
        <div className="flex items-center space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigateMonth(-1)}
            iconName="ChevronLeft"
          />
          <span className="text-sm font-medium text-card-foreground min-w-[120px] text-center">
            {months?.[currentMonth?.getMonth()]} {currentMonth?.getFullYear()}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigateMonth(1)}
            iconName="ChevronRight"
          />
        </div>
      </div>
      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-1 mb-4">
        {/* Day Headers */}
        {daysOfWeek?.map(day => (
          <div key={day} className="p-2 text-center text-xs font-medium text-muted-foreground">
            {day}
          </div>
        ))}
        
        {/* Calendar Days */}
        {days?.map((date, index) => {
          if (!date) {
            return <div key={index} className="p-2"></div>;
          }

          const dateKey = formatDateKey(date);
          const availability = availabilityData?.[dateKey];
          const isAvailable = availability && availability?.available;
          const isPast = isPastDate(date);
          const isSelected = isSelectedDate(date);
          const todayDate = isToday(date);

          return (
            <button
              key={index}
              onClick={() => handleDateClick(date)}
              disabled={isPast || !isAvailable}
              className={`p-2 text-center text-sm rounded-md transition-micro relative ${
                isPast
                  ? 'text-muted-foreground cursor-not-allowed'
                  : isSelected
                  ? 'bg-primary text-primary-foreground'
                  : isAvailable
                  ? 'text-card-foreground hover:bg-muted cursor-pointer'
                  : 'text-muted-foreground cursor-not-allowed'
              } ${todayDate ? 'ring-2 ring-secondary' : ''}`}
            >
              <div className="font-medium">{date?.getDate()}</div>
              {availability && isAvailable && !isPast && (
                <div className="text-xs mt-1">
                  <div className={`${availability?.peak ? 'text-warning' : 'text-success'}`}>
                    {formatCurrency(availability?.price)?.replace('₹', '₹')}
                  </div>
                </div>
              )}
              {!isAvailable && !isPast && (
                <div className="text-xs text-error mt-1">Full</div>
              )}
            </button>
          );
        })}
      </div>
      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-xs">
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-success rounded"></div>
          <span className="text-muted-foreground">Available</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-warning rounded"></div>
          <span className="text-muted-foreground">Peak Season</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-error rounded"></div>
          <span className="text-muted-foreground">Fully Booked</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-secondary rounded"></div>
          <span className="text-muted-foreground">Today</span>
        </div>
      </div>
    </div>
  );
};

export default AvailabilityCalendar;